import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BadgeCheck, Briefcase, Building2, Mail, Phone, Shield, UserRound } from 'lucide-react';

interface ProfileFormData {
  fullName: string;
  email: string;
  phone: string;
  role: string;
  timezone: string;
  department: string;
}

const DEFAULT_PROFILE: ProfileFormData = {
  fullName: 'System Administrator',
  email: 'dev@shieldedhaven.com',
  phone: '+1 (555) 845-1290',
  role: 'super_admin',
  timezone: 'America/Chicago',
  department: 'Platform Operations',
};

export function SystemAdminProfile() {
  const [formData, setFormData] = useState<ProfileFormData>(DEFAULT_PROFILE);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const initials = useMemo(() => {
    const parts = formData.fullName.split(' ').filter(Boolean);
    return parts.map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'SA';
  }, [formData.fullName]);

  const updateField = (field: keyof ProfileFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setSavedAt(new Date().toLocaleString());
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Manage your system administrator account details</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-blue-600 text-white text-xl font-bold flex items-center justify-center">
            {initials}
          </div>
          <div>
            <p className="text-lg font-semibold text-slate-900">{formData.fullName}</p>
            <p className="text-sm text-slate-500">{formData.email}</p>
            <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700">
              <BadgeCheck className="h-4 w-4" />
              <span>Verified account</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Edit details</CardTitle>
            <CardDescription>Keep contact and operational information up to date</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full-name">Full name</Label>
                <Input
                  id="full-name"
                  value={formData.fullName}
                  onChange={(event) => updateField('fullName', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(event) => updateField('email', event.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(event) => updateField('department', event.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(value) => updateField('role', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="security_admin">Security Admin</SelectItem>
                    <SelectItem value="operations_admin">Operations Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select value={formData.timezone} onValueChange={(value) => updateField('timezone', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">America/New_York</SelectItem>
                    <SelectItem value="America/Chicago">America/Chicago</SelectItem>
                    <SelectItem value="America/Denver">America/Denver</SelectItem>
                    <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700 text-white">
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setFormData(DEFAULT_PROFILE)}>
                Reset
              </Button>
              {savedAt && <span className="text-xs text-emerald-600">Saved at {savedAt}</span>}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Account Snapshot</CardTitle>
            <CardDescription>Quick operational context</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-blue-600" />
              <span>Security level: Tier 1</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" />
              <span>Schools supervised: 18</span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-blue-600" />
              <span>Access scope: Global platform</span>
            </div>
            <div className="flex items-center gap-2">
              <UserRound className="h-4 w-4 text-blue-600" />
              <span>Last profile review: 2 days ago</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-600" />
              <span>{formData.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-blue-600" />
              <span>{formData.phone}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
